import CloseButton from 'Components/Common/CloseButton';
import CountrySelect from 'Components/Common/CountrySelect';
import RadioGroup from 'Components/input/RadioGroup/RadioGroup';
import React, { useEffect, useState } from 'react';
import { Offcanvas, OffcanvasBody } from 'reactstrap';
import { defaultGlobalInvitesFilters } from 'slices/invites/reducer';
import { InviteYesNoFilter } from 'types/campaign/invites';
import { GlobalInviteModeFilter, GlobalInvitesFilters } from 'types/invite';

interface AllInviteFiltersProps {
    isOpen: boolean;
    onClose: () => void;
    defaultFilters: GlobalInvitesFilters;
    onApply: (filters: GlobalInvitesFilters) => void;
}

const YES_NO_OPTIONS = [
    { id: 'all', label: 'All' },
    { id: 'y', label: 'Yes' },
    { id: 'n', label: 'No' },
];

const MODE_OPTIONS = [
    { id: 'all', label: 'All' },
    { id: 'managed', label: 'Managed' },
    { id: 'discovery', label: 'Discovery' },
    { id: 'manual', label: 'Manual' },
    { id: 'search', label: 'Search' },
];

const toFilterTileValue = (value?: string) => (value ? value : 'all');

const normalizeYesNo = (value?: string) =>
    value === 'all' || !value ? '' : (value as InviteYesNoFilter);

const AllInviteFilters: React.FC<AllInviteFiltersProps> = ({
    isOpen,
    onClose,
    defaultFilters,
    onApply,
}) => {
    const [filters, setFilters] = useState<GlobalInvitesFilters>(defaultFilters);

    useEffect(() => {
        setFilters(defaultFilters);
    }, [defaultFilters, isOpen]);

    const applyFilters = (next: GlobalInvitesFilters) => {
        onApply({
            ...next,
            page: 1,
            mode: next.mode === 'all' || !next.mode ? '' : next.mode,
            country: next.country || '',
            applied: normalizeYesNo(next.applied),
            collaborated: normalizeYesNo(next.collaborated),
            whatsapp_notified: normalizeYesNo(next.whatsapp_notified),
        });
        onClose();
    };

    return (
        <Offcanvas isOpen={isOpen} toggle={onClose} direction="end">
            <CloseButton onClose={onClose} />
            <OffcanvasBody className="p-0 position-relative">
                <div className="payments-offcanvas-bg" />
                <div className="filters p-4">
                    <div className="form-group filter-item">
                        <label htmlFor="invite-mode" className="filter-label">Mode</label>
                        <RadioGroup
                            name="invite-mode"
                            options={MODE_OPTIONS}
                            defaultValue={toFilterTileValue(filters.mode)}
                            onChange={(mode) =>
                                setFilters({ ...filters, mode: mode as GlobalInviteModeFilter })
                            }
                            selectFirstWhenNoValueProvided={false}
                        />
                    </div>
                    <div className="form-group filter-item">
                        <label htmlFor="invite-country" className="filter-label">Country</label>
                        <CountrySelect
                            value={filters.country || null}
                            onChange={(country) => setFilters({ ...filters, country: country ?? '' })}
                            placeholder="Select country"
                        />
                    </div>
                    <div className="form-group filter-item">
                        <label htmlFor="invite-applied" className="filter-label">Applied?</label>
                        <RadioGroup
                            name="invite-applied"
                            options={YES_NO_OPTIONS}
                            defaultValue={toFilterTileValue(filters.applied)}
                            onChange={(applied) =>
                                setFilters({ ...filters, applied: applied as InviteYesNoFilter })
                            }
                            selectFirstWhenNoValueProvided={false}
                        />
                    </div>
                    <div className="form-group filter-item">
                        <label htmlFor="invite-collaborated" className="filter-label">Collaborated?</label>
                        <RadioGroup
                            name="invite-collaborated"
                            options={YES_NO_OPTIONS}
                            defaultValue={toFilterTileValue(filters.collaborated)}
                            onChange={(collaborated) =>
                                setFilters({
                                    ...filters,
                                    collaborated: collaborated as InviteYesNoFilter,
                                })
                            }
                            selectFirstWhenNoValueProvided={false}
                        />
                    </div>
                    <div className="form-group filter-item">
                        <label htmlFor="invite-whatsapp-notified" className="filter-label">
                            SMS/WhatsApp sent?
                        </label>
                        <RadioGroup
                            name="invite-whatsapp-notified"
                            options={YES_NO_OPTIONS}
                            defaultValue={toFilterTileValue(filters.whatsapp_notified)}
                            onChange={(whatsapp_notified) =>
                                setFilters({
                                    ...filters,
                                    whatsapp_notified: whatsapp_notified as InviteYesNoFilter,
                                })
                            }
                            selectFirstWhenNoValueProvided={false}
                        />
                    </div>
                </div>
                <div className="filters-offcanvas-footer d-flex justify-content-between align-items-center px-3 p-4 gap-2">
                    <button
                        type="button"
                        className="theme-btn theme-btn-lg w-100 btn-primary-outlined"
                        onClick={() => applyFilters({ ...defaultGlobalInvitesFilters })}
                    >
                        Reset
                    </button>
                    <button
                        type="button"
                        className="theme-btn theme-btn-lg w-100 btn-primary"
                        onClick={() => applyFilters(filters)}
                    >
                        Apply
                    </button>
                </div>
            </OffcanvasBody>
        </Offcanvas>
    );
};

export default AllInviteFilters;
